import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, WifiOff, RefreshCw, CheckCircle } from 'lucide-react';
import { getTranslation } from '../languages';

const ConnectionStatus = ({ status, statusData, uiLanguage }) => {
    const t = useCallback((key, params = {}) => getTranslation(key, uiLanguage, params), [uiLanguage]);
    const [visible, setVisible] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        if (!status) return;

        let type, text, icon;
        switch (status) {
            case 'connected':
                // Don't show success message unless recovering from disconnection
                if (message?.type !== 'success') {
                    setVisible(false);
                    return;
                }
                type = 'success';
                text = t('connected');
                icon = <CheckCircle className="w-5 h-5" />;
                break;

            case 'disconnected':
                type = 'warning';
                text = t('connectionLost');
                icon = <WifiOff className="w-5 h-5" />;
                break;

            case 'reconnecting':
                type = 'info';
                text = t('reconnecting', { attempt: statusData?.attempt, maxAttempts: statusData?.maxAttempts });
                icon = <RefreshCw className="w-5 h-5 animate-spin" />;
                break;

            case 'server_restarting':
                type = 'info';
                text = statusData || t('serverUpdating');
                icon = <RefreshCw className="w-5 h-5 animate-spin" />;
                break;

            case 'heartbeat_timeout':
                type = 'warning';
                text = t('connectionTimeout');
                icon = <WifiOff className="w-5 h-5" />;
                break;

            case 'max_retries_exceeded':
                type = 'error';
                text = t('unableToConnect');
                icon = <AlertCircle className="w-5 h-5" />;
                break;

            case 'error':
                type = 'error';
                text = statusData || t('connectionError');
                icon = <AlertCircle className="w-5 h-5" />;
                break;

            default:
                return;
        }

        setMessage({ type, text, icon });
        setVisible(true);

        // Auto-hide success messages after 3 seconds
        if (type === 'success') {
            const timer = setTimeout(() => setVisible(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [status, statusData, message?.type, t]);

    if (!visible || !message) return null;

    const bgColor = {
        success: 'bg-green-50 border-green-200',
        info: 'bg-blue-50 border-blue-200',
        warning: 'bg-yellow-50 border-yellow-200',
        error: 'bg-red-50 border-red-200'
    }[message.type];

    const textColor = {
        success: 'text-green-800',
        info: 'text-blue-800',
        warning: 'text-yellow-800',
        error: 'text-red-800'
    }[message.type];

    const iconColor = {
        success: 'text-green-600',
        info: 'text-blue-600',
        warning: 'text-yellow-600',
        error: 'text-red-600'
    }[message.type];

    return (
        <div className={`fixed top-4 right-4 z-50 transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
            <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${bgColor} ${textColor} shadow-lg`}>
                <div className={iconColor}>
                    {message.icon}
                </div>
                <p className="text-sm font-medium">{message.text}</p>
            </div>
        </div>
    );
};

export default ConnectionStatus;